/**
 * "Confirm it's you" — the second factor, in place, mid-session (A7/G7).
 *
 * Mounted once, in the signed-in shell, and driven entirely by lib/stepUp.ts:
 * no screen has to know it exists. When any request meets 403 `2FA_REQUIRED`
 * (the two-hour window lapsed), the request layer raises this, WAITS, and once
 * a factor is accepted sends the original request again with the same body.
 * Nothing typed is lost and nothing navigates — which is the whole point: the
 * old handling was a full page load to /verify, and a Save two hours into a
 * worksheet threw the worksheet away.
 *
 * It asks for what the account actually has (SecondFactorPanel): the passkey
 * first, the authenticator app next, the emailed code always.
 *
 * Says WHY, not just what. "Confirm it's you" out of nowhere reads as an app
 * being difficult; saying that nothing is lost makes the friction obviously
 * proportionate. Locked while a check is running, so a stray Esc or scrim tap
 * cannot abandon a ceremony the device is halfway through.
 */
import { lazy, Suspense, useEffect, useState } from "react";
import { SecondFactorSkeleton } from "@/components/SecondFactorSkeleton";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
/* Loaded when the dialog first opens — a lapsed 2FA window is rare. */
const SecondFactorPanel = lazy(() => import("@/components/SecondFactorPanel").then((m) => ({ default: m.SecondFactorPanel })));
import { registerStepUpPrompt, settleStepUp } from "@/lib/stepUp";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/hooks/useTranslation";

export function StepUpDialog() {
  const { logout } = useAuth();
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Bumped per prompt so the panel starts fresh every time. */
  const [round, setRound] = useState(0);

  useEffect(
    () =>
      registerStepUpPrompt(() => {
        setBusy(false);
        setRound((r) => r + 1);
        setOpen(true);
      }),
    [],
  );

  // Unmounted with a prompt open (signing out, a route that drops the shell):
  // the waiting requests must hear "no" rather than wait for ever.
  useEffect(
    () => () => {
      settleStepUp("dismissed");
    },
    [],
  );

  const dismiss = () => {
    if (busy) return;
    setOpen(false);
    settleStepUp("dismissed");
  };

  const verified = () => {
    setOpen(false);
    settleStepUp("verified");
  };

  const signOut = () => {
    setOpen(false);
    settleStepUp("dismissed");
    void logout();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : dismiss())}>
      <DialogContent size="sm" fit locked={busy} data-testid="dialog-step-up">
        <DialogHeader>
          <DialogTitle>{t("shellSecurityStepUpTitle", "Confirm It’s You")}</DialogTitle>
          <DialogDescription>
            {t(
              "shellSecurityStepUpBody",
              "For security, SaleFish asks you to confirm it’s you every couple of hours. What you were doing is kept — it carries on as soon as you’ve confirmed.",
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {open && (
            <Suspense fallback={<SecondFactorSkeleton />}>
              <SecondFactorPanel key={round} onVerified={verified} onBusyChange={setBusy} />
            </Suspense>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={signOut} disabled={busy} data-testid="button-step-up-sign-out">
            {t("signOut", "Sign Out")}
          </Button>
          <Button variant="outline" onClick={dismiss} disabled={busy} data-testid="button-step-up-dismiss">
            {t("shellSecurityNotNow", "Not Now")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
